import axios, {AxiosInstance, AxiosRequestConfig, AxiosError} from 'axios';
import {API_ROUTES} from '../../constants/apiRoutes';
import {TokenStore} from '../../Services/security/TokenStore';

let httpInstance: AxiosInstance | null = null;

/**
 * Lazy singleton: create axios instance on first use (not at import time).
 * BaseURL from VITE_API_BASE_URL only; fails fast if missing.
 */
function getHttpInstance (): AxiosInstance {
  if (httpInstance) return httpInstance;

  const baseURL = import.meta.env.VITE_API_BASE_URL;
  if (!baseURL || typeof baseURL !== 'string' || baseURL.trim() === '') {
    throw new Error('VITE_API_BASE_URL is required. Set it in .env or build environment.');
  }

  const instance = axios.create({
    baseURL,
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
    headers: {
      'Accept': 'application/json'
    },
    withCredentials: false
  });

  /** API origin for same-origin check: do not send Authorization to external URLs */
  function getApiOrigin (): string {
    try {
      return new URL(baseURL).origin;
    } catch {
      return '';
    }
  }

  function isExternalUrl (requestUrl: string): boolean {
    if (!requestUrl.startsWith('http://') && !requestUrl.startsWith('https://')) {
      return false;
    }
    try {
      return new URL(requestUrl).origin !== getApiOrigin();
    } catch {
      return true;
    }
  }

  async function refreshAccessToken (): Promise<string | null> {
    const refreshToken = TokenStore.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await instance.post(API_ROUTES.AUTH.REFRESH_TOKEN, {refreshToken});
      if (response.data.stat_code === 200) {
        const {accessToken: newAccess} = response.data.data || {};
        if (newAccess) {
          TokenStore.setAccessToken(newAccess);
          return newAccess;
        }
      }
      return null;
    } catch {
      TokenStore.clear();
      return null;
    }
  }

  instance.interceptors.request.use(
    (config) => {
      const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
      if (isFormData && config.headers) {
        delete (config.headers as Record<string, string>)['Content-Type'];
      }

      if (!isExternalUrl(config.url ?? '')) {
        const token = TokenStore.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const csrfHeader = import.meta.env.VITE_CSRF_TOKEN_HEADER;
      if (csrfToken && csrfHeader) {
        config.headers[csrfHeader] = csrfToken;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      if (process.env.NODE_ENV === 'development') {
        const deprecatedEndpoint = response.headers['x-deprecated-endpoint'];
        const deprecatedMessage = response.headers['x-deprecated-message'];
        if (deprecatedEndpoint || deprecatedMessage) {
          console.warn('[Deprecated Endpoint Warning]', {
            endpoint: deprecatedEndpoint || 'Unknown',
            message: deprecatedMessage || 'This endpoint is deprecated',
            url: response.config.url,
            method: response.config.method?.toUpperCase()
          });
        }
      }
      return response;
    },
    async (error: AxiosError) => {
      if (error.response?.status === 401) {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        if (originalRequest && !originalRequest._retry) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
        }
        TokenStore.clear();
        const reqUrl = (originalRequest?.url || '').toString();
        if (!reqUrl.includes('/login') && !reqUrl.includes('/auth/refresh')) {
          window.location.href = '/login';
        }
      } else if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'];
        if (retryAfter) {
          await new Promise(resolve => setTimeout(resolve, Number(retryAfter) * 1000));
          return instance(error.config!);
        }
      } else if (error.response?.status === 404) {
        const featureDisabledError = error as AxiosError & { isFeatureDisabled?: boolean };
        featureDisabledError.isFeatureDisabled = true;
        return Promise.reject(featureDisabledError);
      }
      return Promise.reject(error);
    }
  );

  httpInstance = instance;
  return instance;
}

/** Call on app bootstrap when refresh token exists but no access token (e.g. after reload). */
export async function tryRestoreSession (): Promise<boolean> {
  const refreshToken = TokenStore.getRefreshToken();
  if (!refreshToken) return false;
  try {
    const response = await getHttpInstance().post(API_ROUTES.AUTH.REFRESH_TOKEN, {refreshToken});
    if (response.data?.stat_code === 200 && response.data?.data?.accessToken) {
      TokenStore.setAccessToken(response.data.data.accessToken);
      return true;
    }
  } catch {
    TokenStore.clear();
  }
  return false;
}

type HTTPRequestConfig = AxiosRequestConfig;

/** Http client: lazy singleton (instance created on first request, not at import time). */
export const Http = {
  get: <T>(url: string, config: HTTPRequestConfig = {}) => getHttpInstance().get<T>(url, config),
  delete: <T>(url: string, config: HTTPRequestConfig = {}) => getHttpInstance().delete<T>(url, config),
  put: <T>(url: string, body: unknown, config: HTTPRequestConfig = {}) => getHttpInstance().put<T>(url, body, config),
  patch: <T>(url: string, body: unknown, config: HTTPRequestConfig = {}) => getHttpInstance().patch<T>(url, body, config),
  post: <T>(url: string, body: unknown, config: HTTPRequestConfig = {}) => getHttpInstance().post<T>(url, body, config)
};